'use strict';

customerZone

/**
 * A singleton containing the currently authenticated user, and his details.
 * The current session will be kept in sync with local storage.
 **/
    .service('Session', ['$log', '$timeout', '$rootScope', 'Storage', 'utils', function ($log, $timeout, $rootScope,
        Storage, utils) {

        var self = this;
        this.username = null;
        this.token = null;
        var _profile = null;
        var _subscriber = null;
        var _subscribers = [];
        var _flags = {};
        var _refreshHandler = function(){};

        self.PROFILE_CHANGED_EVENT = 'profileChanged';
        self.SUBSCRIPTION_CHANGED_EVENT = 'subscriptionChanged';
        self.FLAGS_CHANGED_EVENT = 'flagsChanged';

        /**
         * Create a new session for a given username and auth token, and set it as the current session.
         */
        this.create = function (username, token, refreshToken) {
            this.username = username;
            this.token = token;
            this.refreshToken = refreshToken;
            _profile = {};
            _subscribers = [];
            _subscriber = null;
            Storage.setItem('credentials', JSON.stringify({
                username: username,
                token: token,
                refreshToken: refreshToken
            }));
            Storage.setItem('profile', marshalProfile(null));
            Storage.setItem('subscriber', marshalSubscriber(null));
            Storage.setItem('subscribers', marshalSubscribers(null));
        };

        this.updateToken = function(token, refreshToken){
            this.token = token;
            this.refreshToken = refreshToken;
            Storage.setItem('credentials', JSON.stringify({
                username: this.username,
                token: token,
                refreshToken: refreshToken
            }));
        };

        /**
         * Destroy the current session.
         */
        this.destroy = function () {
            if (!self.authenticated()) return;
            $log.debug('Destroying session');
            this.username = null;
            this.token = null;
            _profile = null;
            _subscriber = null;
            _subscribers = undefined;
            _flags = {};
            Storage.removeItem('credentials');
            Storage.removeItem('profile');
            Storage.removeItem('subscribers');
            Storage.removeItem('subscriber');
            Storage.removeItem('flags');
            $rootScope.$broadcast(self.PROFILE_CHANGED_EVENT, null);
            $rootScope.$broadcast(self.SUBSCRIPTION_CHANGED_EVENT, null);
            $rootScope.$broadcast(self.FLAGS_CHANGED_EVENT);
        };

        /**
         * Get/set the profile in the current session.
         * if no session is active, this function has no effect.
         */
        this.profile = function(value) {
            if (arguments.length > 0){
                if (!this.authenticated())
                    value = null;
                if (_profile === value) return;
                _profile = value;
                Storage.setItem('profile', marshalProfile(value));
                $rootScope.$broadcast(self.PROFILE_CHANGED_EVENT, value);
            }
            return _profile;
        };

        /**
         * Get/set arbitrary flags in the session
         */
        this.flags = function(key, value) {
            if (value !== undefined){
                if (!this.authenticated())
                    return false;
                if (_flags[key] === value) return;
                _flags[key] = value;
                Storage.setItem('flags', JSON.stringify(_flags));
                $rootScope.$broadcast(self.FLAGS_CHANGED_EVENT, key, value);
            }
            return _flags[key];
        };

        /**
         * Get/set whether the current session is impersonating
         */
        this.impersonating = function(value){
            return self.flags('impersonating', value);
        };

        /**
         * Get/set whether the current session is using a breached password
         */
        this.passwordLeaked = function(value){
            return self.flags('password_leaked', value);
        };

        /**
         * Get/set the subscriptions of the current session.
         * If no session is active, this function has no effect.
         *
         * This is a temporary wrapper around subscribers. Setting is no longer supported.
         */
        this.subscription = function(){
            return _subscriber ? _subscriber.active : null;
        };

        /**
         * Get/set the subscribers of the current session.
         * If no session is active, this function has no effect.
         * @param value subscribers to set.  if omitted, return the current subscribers
         * @param msisdn if set, preselect the subscriber with that msisdn.
         */
        this.subscribers = function(value, msisdn){
            msisdn = utils.checkMsisdn(msisdn).normalized;
            if (arguments.length > 0){
                if (!this.authenticated())
                    value = [];
                if (_subscribers === value) return;
                _subscribers = value;
                Storage.setItem('subscribers', marshalSubscribers(value));
                this.subscriber(selectDefaultSubscriber(_subscribers, msisdn));
            }
            var result = [];
            result.push.apply(result, _subscribers);
            return result;
        };

        this.subscriber = function(value){
            if (arguments.length > 0){
                if (!this.authenticated())
                    value = null;
                if (!value) value = selectDefaultSubscriber(_subscribers);
                if (value && _subscribers.filter(function(c){ return c.msisdn === value.msisdn; }).length === 0)
                    value = selectDefaultSubscriber(_subscribers);
                _subscriber = value;
                Storage.setItem('subscriber', marshalSubscriber(value));
                $rootScope.$broadcast(self.SUBSCRIPTION_CHANGED_EVENT, _subscriber ? _subscriber.active : undefined);
            }
            return _subscriber;
        };

        var selectDefaultSubscriber = function(subscribers, msisdn){
            if (!subscribers) return null;

            var matchedSubscriber = subscribers
                .filter(function(c){ return !!_subscriber && c.msisdn === _subscriber.msisdn; });
            if (matchedSubscriber.length) return matchedSubscriber[0];

            var matchedMsisdn =  subscribers.filter(function(c){ return !!msisdn && c.msisdn === msisdn; });
            if (matchedMsisdn.length) return matchedMsisdn[0];

            var enabled = subscribers.filter(function(c){ return c.state === jim.SubscriberState.enabled; });
            if (enabled.length) return enabled[0];

            return subscribers[0] || null;
        };

        /**
         * Initialize a session from storage.
         */
        this.loadFromStorage = function(){

            try {
                var credentials = Storage.getItem('credentials');
                if (credentials && (credentials = JSON.parse(credentials))) {
                    this.username = credentials.username;
                    this.token = credentials.token;
                    this.refreshToken = credentials.refreshToken;
                    _profile = unmarshalProfile(Storage.getItem('profile'));
                    _subscribers = unmarshalSubscribers(Storage.getItem('subscribers'));
                    _subscriber = unmarshalSubscriber(Storage.getItem('subscriber'));
                    _flags = JSON.parse(Storage.getItem('flags') || '{}') || {};

                    $log.debug('Session loaded from storage' + JSON.stringify({
                        username: this.username,
                        tokenLength: (this.token ? this.token.length : 0),
                        refreshTokenLength: (this.refreshToken ? this.refreshToken.length : 0),
                        profile: _profile
                    }));
                    this.refresh();
                    $rootScope.$broadcast(self.PROFILE_CHANGED_EVENT, _profile);
                    $rootScope.$broadcast(self.SUBSCRIPTION_CHANGED_EVENT,
                        _subscriber ? _subscriber.active : null);
                }
            }
            catch (e){
                $log.warn('Could not load state: '+e);
                this.refresh();
            }
        };

        /**
         * @returns whether a user is currently authenticated
         */
        this.authenticated = function(){
            return !!this.username;
        };

        /**
         * Set a handler that will be called when the session requires refresh.
         */
        this.setRefreshHandler = function(handler){
            _refreshHandler = handler;
        };

        var ongoingRefresh;

        /**
         * Refresh the current session.  This loads fresh values for Profile and Subscription.
         *
         * The actual refreshing is delegated to a pluggable handler.
         */
        this.refresh = function(){
            if (!ongoingRefresh){
                // make sure this happens in the next cycle, in case the handler still needs to be set
                ongoingRefresh = $timeout(function(){
                    if (!self.authenticated()) {
                        $log.debug('refresh cancelled - no active session');
                        return;
                    }
                    return _refreshHandler();
                }, 1).then(function(){
                    ongoingRefresh = undefined;
                });
            }
            return ongoingRefresh;
        };

        var marshalSubscriber = function(subscriber){
            if (!subscriber) return JSON.stringify(null);
            var prepSubscription = function(subscription){
                if (!subscription) return JSON.stringify(null);
                var result = angular.copy(subscription);
                result.validFrom = marshalDate(result.validFrom);
                result.validUntil = marshalDate(result.validUntil);
                return result;
            };
            return JSON.stringify({
                msisdn: subscriber.msisdn,
                subscriptions: prepSubscription(subscriber.subscription)
            });
        };

        var marshalSubscribers = function(subscribers){
            if (!subscribers) return 'null';

            var prepSubscriber = function(subscriber){
                if (!subscriber) return JSON.stringify(null);
                var marshalSubscription = function(subscription){
                    if (!subscription) return JSON.stringify(null);
                    var result = angular.copy(subscription);
                    result.validFrom = marshalDate(result.validFrom);
                    result.validUntil = marshalDate(result.validUntil);
                    return result;
                };
                return {
                    msisdn: subscriber.msisdn,
                    subscriptions: marshalSubscription(subscriber.subscription)
                };
            };

            return JSON.stringify(subscribers.map(prepSubscriber));
        };

        var marshalProfile = function(profile){
            if (!profile) return JSON.stringify(null);
            var result = angular.copy(profile);
            result.birthDate = marshalDate(result.birthDate);
            result.verifiedUntil = marshalDateTime(result.verifiedUntil);
            return JSON.stringify(result);
        };

        var unmarshalSubscriber = function(string){
            var unmarshalSubscription = function(payload){
                payload.validUntil = unmarshalDate(payload.validUntil);
                payload.validFrom = unmarshalDate(payload.validFrom);
                payload.product = payload.product ? jim.Product.fromProperties(payload.product) : undefined;
                return jim.Subscription.fromObject(payload);
            };

            if (!string || string === '' || string === 'null') return undefined;
            var result = JSON.parse(string);
            return new jim.Subscriber(unmarshalSubscription(result.subscriptions));
        };

        var unmarshalSubscribers = function(string){
            if (!string || string === '' || string === 'null') return [];
            var result = JSON.parse(string);
            return result.map(function(entry){ return unmarshalSubscriber(JSON.stringify(entry)); });
        };

        var unmarshalProfile = function(string){
            if (!string || string === '' || string === 'null') return undefined;
            var result = JSON.parse(string);
            result.birthDate = unmarshalDate(result.birthDate);
            result.verifiedUntil = unmarshalDateTime(result.verifiedUntil);
            return jim.Subscription.fromObject(result);
        };

        var marshalDate = function(date){
            if (!date) return undefined;
            return date.utc().toISOString().split('T')[0];
        };

        var unmarshalDate = function(string){
            if (!string || string === '') return undefined;
            return moment.utc(string, 'YYYY-MM-DD');
        };

        var marshalDateTime = function(date){
            if (!date) return undefined;
            return date.utc().toISOString();
        };

        var unmarshalDateTime = function(string){
            if (!string || string === '') return undefined;
            return moment.utc(string, 'YYYY-MM-DDThh:mm:ssZ');
        };

        self.loadFromStorage();
    }]);
